// Internal helpers shared by the per-engine projectors in
// `schemaCacheAdapter.ts`. Folding, QualifiedName construction, routine
// mapping, and Column row building.

import type { QualifiedName, SqlEngine } from '../../sql-grammar/model';
import type { Column, FkEdge, JsonClassification, Routine, RoutineKind, RoutineParam, RoutineParamMode } from '../types';
import type { RoutineMetadata, RoutineParameter } from '@/types/electron';
import type { RoutineMeta } from '@/core/engines/intellisenseTypes';
import { compareFkConstraints } from '@/features/workspace/common/utils/looseFkResolver';
import { matchColumnName } from '@/features/workspace/common/utils/columnNameMatch';
import { classifyJsonColumn } from '../json/classify/classifyJsonColumn';
import { sameRelation } from './snapshotRelations';

export type FoldFn = (value: string) => string;

export function foldFor(engine: SqlEngine): FoldFn {
  if (engine === 'oracle') return (v) => v.toUpperCase();
  return (v) => v.toLowerCase();
}

export function qn(name: string, schema: string | undefined, fold: FoldFn): QualifiedName {
  const base: QualifiedName = {
    name,
    foldedName: fold(name),
    quoted: false,
    raw: schema ? `${schema}.${name}` : name,
  };
  if (schema) {
    base.schema = schema;
    base.foldedSchema = fold(schema);
  }
  return base;
}

function mapParamMode(isOutput: boolean | undefined): RoutineParamMode {
  return isOutput ? 'out' : 'in';
}

function mapParam(p: RoutineParameter): RoutineParam {
  return {
    name: p.name,
    dataType: p.dataType,
    mode: mapParamMode(p.isOutput),
    hasDefault: false,
  };
}

function routineKindFor(routineType: string | undefined): RoutineKind {
  const t = (routineType ?? '').toUpperCase();
  if (t.includes('PROCEDURE')) return 'procedure';
  if (t.includes('AGGREGATE')) return 'aggregate';
  if (t.includes('WINDOW')) return 'window';
  if (t.includes('TABLE')) return 'table-function';
  return 'scalar-function';
}

export function projectRoutines(
  byNameLower: Record<string, RoutineMetadata[]>,
  engine: SqlEngine,
): Map<string, Routine[]> {
  const fold = foldFor(engine);
  const out = new Map<string, Routine[]>();
  for (const metadataList of Object.values(byNameLower)) {
    for (const meta of metadataList) {
      const qualified = qn(meta.name, meta.schema || undefined, fold);
      const routine: Routine = {
        signature: {
          qualified,
          kind: routineKindFor(meta.routineType),
          params: meta.parameters.map(mapParam),
        },
      };
      const key = qualified.foldedName;
      const existing = out.get(key);
      if (existing) existing.push(routine);
      else out.set(key, [routine]);
    }
  }
  return out;
}

function mssqlRoutineKindFor(kind: RoutineMeta['kind']): RoutineKind {
  switch (kind) {
    case 'procedure':
      return 'procedure';
    case 'aggregate':
      return 'aggregate';
    case 'window':
      return 'window';
    case 'function':
    default:
      // TVF parameter-shape sniffing is not attempted in this plan — every
      // 'function' kind maps to 'scalar-function' (see plan17 decision #1).
      return 'scalar-function';
  }
}

function mapMssqlRoutineParam(p: RoutineMeta['parameters'][number]): RoutineParam {
  return { name: p.name, dataType: p.dataType, mode: p.mode, hasDefault: false };
}

/**
 * MSSQL-specific routine mapper: `RoutineMeta` (intellisense shape, already
 * flat — one entry per routine, not grouped by name-lower) does not fit
 * `projectRoutines`'s `Record<string, RoutineMetadata[]>` input shape used by
 * pg/mysql/oracle, so it gets its own small mapper instead of a force-fit.
 */
export function projectMssqlRoutines(
  routines: readonly RoutineMeta[] | undefined,
): Map<string, Routine[]> {
  const fold = foldFor('mssql');
  const out = new Map<string, Routine[]>();
  if (!routines) return out;
  for (const meta of routines) {
    const qualified = qn(meta.name, meta.schema, fold);
    const routine: Routine = {
      signature: {
        qualified,
        kind: mssqlRoutineKindFor(meta.kind),
        params: meta.parameters.map(mapMssqlRoutineParam),
      },
    };
    const key = qualified.foldedName;
    const existing = out.get(key);
    if (existing) existing.push(routine);
    else out.set(key, [routine]);
  }
  return out;
}

type ColumnLike = {
  readonly name: string;
  readonly dataType: string;
  /** PostgreSQL `format_type` spelling (`PgSchemaColumn.formattedType`). */
  readonly formattedType?: string;
  readonly isPrimaryKey?: boolean;
  readonly isNullable?: boolean;
  /** `pg_enum` members of a native PG enum column (`PgSchemaColumn`). */
  readonly enumValues?: readonly string[];
};

/**
 * Per-column hints fed into JSON classification. Set entries are populated by:
 * - `isJsonCheckedColumns`: per-engine CHECK-constraint detectors
 *   (`json/classify/checkIsJsonDetection/`).
 * - `looseJsonDeclaredColumns`: MetaInfo `looseJsonColumns` for this table.
 *
 * Column names match the cache's column.name verbatim (case-sensitive). The
 * cascade router applies them in JCS-2 precedence order (CHECK > declaration);
 * engine-native dataType detection always wins regardless of hints.
 */
export type ColumnClassificationHints = {
  readonly isJsonCheckedColumns?: ReadonlySet<string>;
  readonly looseJsonDeclaredColumns?: ReadonlySet<string>;
};

/** A single FROM-side column's FK target: the referenced column name plus
 *  the referenced table's qualified name (`FkEdge.toTable`, schema included). */
export type ColumnFkRef = {
  readonly refColumn: string;
  readonly refTable: QualifiedName;
  /** The column's pairing inside a MULTI-column constraint (see `Column.foreignKeyIsComposite`). */
  readonly composite?: true;
};

/**
 * Per-relation FK lookup keyed by the FROM-side column's EXACT name — the
 * relation's own spelling when its column names are given. Never the folded
 * name: PostgreSQL's `"Ref"` and `ref` are two columns with their own FKs.
 */
export type ColumnFkIndex = ReadonlyMap<string, ColumnFkRef>;

/**
 * Build a per-relation FK lookup from a projector's already-computed
 * `FkEdge[]`. The input may span multiple tables (pg/oracle/mssql compute
 * `fks` once for the whole snapshot) or be pre-scoped to one table
 * (mysql/sqlite compute `fks` per object) — either way, edges are filtered
 * down to the ones whose `fromTable` is the SAME relation being projected
 * (`sameRelation`: name AND schema, so `mia_s1.orders`'s FK never flags a
 * column of `mia_s2.orders`; an edge without a schema matches by name).
 * Composite keys align by array index: `fromColumns[i]` references
 * `toColumns[i]` from the SAME edge; a mismatched-length pair only walks the
 * shorter of the two arrays (defensive — real FK introspection always
 * produces equal-length arrays). An edge column spelled another way than the
 * relation's own (`columnNames`) keys the one column it names under `fold`
 * (`matchColumnName`), never an ambiguous one.
 */
export function buildFkColumnIndex(
  relation: QualifiedName,
  fks: readonly FkEdge[],
  fold: FoldFn,
  columnNames?: readonly string[],
): ColumnFkIndex {
  const index = new Map<string, ColumnFkRef>();
  // A column's target is the first edge covering it in the order every FK
  // surface shares (`compareFkConstraints`: a single-column constraint before
  // a composite one it also belongs to, then by constraint name).
  const own = fks
    .filter((fk) => sameRelation(fk.fromTable, relation))
    .sort((a, b) =>
      compareFkConstraints(
        { name: a.name, columnCount: a.fromColumns.length },
        { name: b.name, columnCount: b.fromColumns.length },
      ),
    );
  for (const fk of own) {
    const len = Math.min(fk.fromColumns.length, fk.toColumns.length);
    const composite = fk.fromColumns.length > 1;
    for (let i = 0; i < len; i++) {
      const from = fk.fromColumns[i];
      const key = (columnNames ? matchColumnName(from, columnNames, fold) : undefined) ?? from;
      if (index.has(key)) continue;
      index.set(key, { refColumn: fk.toColumns[i], refTable: fk.toTable, ...(composite ? { composite: true as const } : {}) });
    }
  }
  return index;
}

/**
 * Merge per-column FK edges back into one edge per constraint. MySQL
 * (`KEY_COLUMN_USAGE`) and SQLite (`PRAGMA foreign_key_list`) introspect ONE
 * row per FK column; `constraintKeys[i]` names the constraint of `edges[i]`
 * (MySQL's constraint name, SQLite's pragma `id`), so the columns of a
 * composite constraint come back together — the shape the PostgreSQL /
 * Oracle / SQL Server projectors already build — and `columnsFor` can mark
 * them `foreignKeyIsComposite`. Row order is kept, so `fromColumns[i]` still
 * pairs with `toColumns[i]`.
 */
export function mergeFkEdgesByConstraint(edges: readonly FkEdge[], constraintKeys: readonly string[]): FkEdge[] {
  const merged = new Map<string, FkEdge>();
  edges.forEach((edge, i) => {
    const key = constraintKeys[i] ?? `#${i}`;
    const prev = merged.get(key);
    merged.set(
      key,
      prev
        ? { ...prev, fromColumns: [...prev.fromColumns, ...edge.fromColumns], toColumns: [...prev.toColumns, ...edge.toColumns] }
        : edge,
    );
  });
  return [...merged.values()];
}

export function columnsFor(
  relation: QualifiedName,
  columns: readonly ColumnLike[],
  fold: FoldFn,
  engine: SqlEngine,
  hints?: ColumnClassificationHints,
  fks?: readonly FkEdge[],
): Column[] {
  const checked = hints?.isJsonCheckedColumns;
  const declared = hints?.looseJsonDeclaredColumns;
  const fkIndex =
    fks && fks.length > 0 ? buildFkColumnIndex(relation, fks, fold, columns.map((c) => c.name)) : undefined;
  return columns.map((c) => {
    const hasIsJsonCheck = checked?.has(c.name) === true;
    // MetaInfo declarations are user-typed and round-trip through import/export
    // — they may not match the cache's column-name case verbatim. Fold both
    // sides so case-insensitive lookups work across engines (Oracle uppercase,
    // others lowercase by `foldFor`).
    const hasLooseJsonDeclaration = declared?.has(fold(c.name)) === true;
    const classification = classifyJsonColumn(c.dataType, engine, {
      hasIsJsonCheck,
      hasLooseJsonDeclaration,
    });
    const fkRef = fkIndex?.get(c.name);
    const col: Column = {
      qualified: qn(c.name, undefined, fold),
      dataType: c.dataType,
      ...(c.formattedType ? { formattedType: c.formattedType } : {}),
      nullable: c.isNullable ?? false,
      isPrimaryKey: c.isPrimaryKey ?? false,
      isForeignKey: fkRef !== undefined,
      relation,
      ...(c.enumValues && c.enumValues.length > 0 ? { isNativeEnumType: true } : {}),
      ...(fkRef
        ? {
            foreignKeyRef: qn(fkRef.refColumn, undefined, fold),
            foreignKeyRefTable: fkRef.refTable,
            ...(fkRef.composite ? { foreignKeyIsComposite: true } : {}),
          }
        : {}),
    };
    return attachJsonClassification(col, classification);
  });
}

function attachJsonClassification(col: Column, classification: JsonClassification): Column {
  if (classification.kind === 'not-json') return col;
  return { ...col, jsonClassification: classification };
}
